import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getSchedules } from "../../../helpers/auth_aws_helper";
import {
  CloudProviderContext,
  getAccountCloudType,
  shouldShowAzurePlaceholderView,
} from "../../../CloudProviderContext";
import SuppressionFilters from "../Suppression/components/SuppressionFilters";
import { formatScheduleName, getSuppressionAccountLabels } from "../Suppression/utils/scheduleNameFormatter";
import { ACCOUNT_DETAIL_FONT_FAMILY, ACCOUNT_DETAIL_FONT_SX } from "../../../components/AccountDetailPolicy/accountDetailTypography";
import {
  AccountDetailTooltip,
  TruncatedTooltipText,
} from "../../../components/AccountDetailPolicy/editAccountTooltipStyles";
import { ErrorBanner } from "./ui";
import { SCHEDULE_STORAGE_KEY, readPagedList, readStoredPageSize, storePageSize } from "./utils";

const PAGE_SIZE_KEY = "cspm_configuration_schedule_page_size";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const API_PAGE_SIZE = 100;
const MAX_API_PAGES = 20;

const CELL_TEXT = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

const SEARCH_FIELD_SX = {
  flex: { xs: "1 1 160px", sm: "0 0 auto" },
  width: { xs: "auto", sm: 220 },
  minWidth: { xs: 0, sm: 220 },
  "& .MuiOutlinedInput-root": {
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "white",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

const CHECK_CHIP_SX = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  height: 24,
  minWidth: "auto",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: "6px",
  backgroundColor: "#eff6ff",
  color: "#2563eb",
  cursor: "pointer",
  "&:hover": { backgroundColor: "#dbeafe" },
  "& .MuiChip-label": { px: 1, whiteSpace: "nowrap" },
};

const MULTI_BTN_SX = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  minWidth: "auto",
  px: 1.5,
  py: 0.5,
  height: 28,
  borderRadius: "6px",
  color: "#334155",
  borderColor: "#e2e8f0",
  backgroundColor: "#f8fafc",
  "&:hover": {
    borderColor: "#cbd5e1",
    backgroundColor: "#f1f5f9",
  },
};

async function fetchSchedulePage(page) {
  const response = await getSchedules({ page, page_size: API_PAGE_SIZE });
  return readPagedList(response, "Unable to load schedules.");
}

const ScheduleList = () => {
  const history = useHistory();
  const { selectedProvider } = useContext(CloudProviderContext);
  const isAzureView = shouldShowAzurePlaceholderView(selectedProvider);
  const accountLabels = getSuppressionAccountLabels(isAzureView);
  const accounts = useSelector((state) => state.acctList?.accounts);

  const [selectedAccountNumber, setSelectedAccountNumber] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => readStoredPageSize(PAGE_SIZE_KEY, 25, PAGE_SIZE_OPTIONS));
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [scheduleModal, setScheduleModal] = useState(null);

  const providerAccountNumbers = useMemo(() => {
    if (!Array.isArray(accounts)) return new Set();
    return new Set(
      accounts
        .filter(
          (account) =>
            account?.accountNumber &&
            !String(account.accountNumber).includes("INIT") &&
            getAccountCloudType(account) === selectedProvider
        )
        .map((account) => String(account.accountNumber))
    );
  }, [accounts, selectedProvider]);

  const availableAccountNumbers = useMemo(() => {
    const accountSet = new Set();
    schedules.forEach((schedule) => {
      const accountNumber = schedule.account_number != null ? String(schedule.account_number) : "";
      if (accountNumber && providerAccountNumbers.has(accountNumber)) {
        accountSet.add(accountNumber);
      }
    });
    return Array.from(accountSet).sort();
  }, [schedules, providerAccountNumbers]);

  useEffect(() => {
    if (
      selectedAccountNumber &&
      providerAccountNumbers.size > 0 &&
      !providerAccountNumbers.has(String(selectedAccountNumber))
    ) {
      setSelectedAccountNumber("");
      setPage(1);
    }
  }, [providerAccountNumbers, selectedAccountNumber]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      let loaded = false;
      setLoading(true);
      setError(null);
      setSchedules([]);
      try {
        const first = await fetchSchedulePage(1);
        if (cancelled) return;
        let items = first.items;
        loaded = true;
        setSchedules(items);
        setLoading(false);

        const pages = Math.min(Math.ceil((first.total || 0) / API_PAGE_SIZE), MAX_API_PAGES);
        for (let nextPage = 2; nextPage <= pages; nextPage += 1) {
          const next = await fetchSchedulePage(nextPage);
          if (cancelled) return;
          const seen = new Set(items.map((item) => item.schedule_id));
          items = items.concat(next.items.filter((item) => item.schedule_id && !seen.has(item.schedule_id)));
          setSchedules(items);
        }
      } catch (loadError) {
        if (!cancelled && !loaded) setError(loadError.message || "Unable to load schedules.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const groupedAccounts = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const filtered = schedules.filter((schedule) => {
      const accountNumber = schedule.account_number != null ? String(schedule.account_number) : "";
      if (!providerAccountNumbers.has(accountNumber)) return false;
      if (selectedAccountNumber && accountNumber !== String(selectedAccountNumber)) return false;
      if (!query) return true;
      const displayName = formatScheduleName(schedule).toLowerCase();
      return displayName.includes(query) || accountNumber.toLowerCase().includes(query);
    });

    const accountMap = new Map();
    filtered.forEach((schedule) => {
      const accountNumber = String(schedule.account_number);
      if (!accountMap.has(accountNumber)) {
        accountMap.set(accountNumber, {
          account_number: accountNumber,
          recurring: [],
          onDemand: [],
          tagBased: [],
        });
      }
      const accountData = accountMap.get(accountNumber);
      if (schedule.resource_scope_type === "tag_based") {
        accountData.tagBased.push(schedule);
      } else if (schedule.resource_scope_type === "full") {
        if (schedule.schedule_type === "recurring") {
          accountData.recurring.push(schedule);
        } else if (schedule.schedule_type === "on_demand") {
          accountData.onDemand.push(schedule);
        }
      }
    });

    const rows = Array.from(accountMap.values()).sort((left, right) =>
      left.account_number.localeCompare(right.account_number)
    );
    const startIndex = (page - 1) * pageSize;
    return {
      items: rows.slice(startIndex, startIndex + pageSize),
      total: rows.length,
    };
  }, [schedules, providerAccountNumbers, selectedAccountNumber, searchTerm, page, pageSize]);

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(groupedAccounts.total / pageSize) || 1);
    if (page > lastPage) setPage(lastPage);
  }, [groupedAccounts.total, pageSize, page]);

  const openSchedule = (schedule) => {
    if (!schedule?.schedule_id) return;
    sessionStorage.setItem(SCHEDULE_STORAGE_KEY, JSON.stringify(schedule));
    history.push({
      pathname: `/configuration/cspm/schedules/${encodeURIComponent(schedule.schedule_id)}`,
      state: { schedule },
    });
  };

  const renderChecksChip = (schedule, closeModal) => {
    if (!schedule?.schedule_id) return null;
    return (
      <AccountDetailTooltip title="CSPM checks">
        <Chip
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <span>Checks</span>
              <VisibilityIcon sx={{ fontSize: 14, color: "#2563eb" }} />
            </Box>
          }
          size="small"
          onClick={() => {
            if (closeModal) closeModal();
            openSchedule(schedule);
          }}
          sx={CHECK_CHIP_SX}
        />
      </AccountDetailTooltip>
    );
  };

  const renderScheduleCell = (accountNumber, scheduleList, title, typeLabel) => {
    if (!scheduleList || scheduleList.length === 0) {
      return <Typography sx={{ ...CELL_TEXT, color: "#94a3b8" }}>—</Typography>;
    }
    if (scheduleList.length === 1) return renderChecksChip(scheduleList[0]);
    return (
      <Button
        size="small"
        variant="outlined"
        onClick={() => setScheduleModal({ title, schedules: scheduleList, accountNumber })}
        sx={MULTI_BTN_SX}
      >
        {scheduleList.length} {typeLabel}
      </Button>
    );
  };

  const totalPages = groupedAccounts.total ? Math.ceil(groupedAccounts.total / pageSize) : 0;
  const pageStart = groupedAccounts.items.length ? (page - 1) * pageSize + 1 : 0;
  const pageEnd = groupedAccounts.items.length ? Math.min(page * pageSize, groupedAccounts.total) : 0;
  const accountsReady = Array.isArray(accounts);
  const showTable = accountsReady && !loading && !error;
  const showLoading = !error && !showTable && (loading || !accountsReady);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2.5 },
        backgroundColor: "#f3f4f6",
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      <Box sx={{ pb: 1.25, mb: 1.75, borderBottom: "1px solid #e5e7eb" }}>
        <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, fontWeight: 700, color: "#111827", fontSize: "1rem" }}>
          CSPM Configuration
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
            color: "#64748b",
            fontSize: "0.875rem",
            fontWeight: 400,
            lineHeight: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          Manage CSPM check suppressions by account and schedule.
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", sm: "auto" },
            flexShrink: 0,
          }}
        >
          <TextField
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={SEARCH_FIELD_SX}
          />
          <SuppressionFilters
            availableAccountNumbers={availableAccountNumbers}
            selectedAccountNumber={selectedAccountNumber}
            onAccountChange={(accountNumber) => {
              setSelectedAccountNumber(accountNumber);
              setPage(1);
            }}
            isAzureView={isAzureView}
          />
        </Box>
      </Box>

      {showLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            backgroundColor: "#fff",
          }}
        >
          <CircularProgress size={32} sx={{ color: "#64748b" }} />
          <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, ml: 2, color: "#64748b", fontSize: "0.875rem", fontWeight: 500 }}>
            Loading schedules...
          </Typography>
        </Box>
      ) : null}

      <ErrorBanner message={!loading ? error : null} onRetry={() => setReloadKey((value) => value + 1)} />

      {showTable ? (
        <Box
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            backgroundColor: "#fff",
            overflow: "hidden",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <TableContainer sx={{ width: "100%", maxWidth: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <Table size="small" sx={{ width: "100%", minWidth: 760, tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "34%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "22%" }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ ...HEADER_CELL_SX, pl: 2 }}>{accountLabels.numberHeader}</TableCell>
                  <TableCell align="center" sx={HEADER_CELL_SX}>Recurring</TableCell>
                  <TableCell align="center" sx={HEADER_CELL_SX}>On Demand</TableCell>
                  <TableCell align="center" sx={HEADER_CELL_SX}>Tag Based</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedAccounts.items.length > 0 ? (
                  groupedAccounts.items.map((accountData) => (
                    <TableRow key={accountData.account_number} sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                      <TableCell align="left" sx={{ ...BODY_CELL_SX, pl: 2 }}>
                        <Typography
                          sx={{
                            ...CELL_TEXT,
                            fontWeight: 600,
                            color: "#0f172a",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={accountData.account_number}
                        >
                          {accountData.account_number}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        {renderScheduleCell(accountData.account_number, accountData.recurring, "Recurring Schedules", "schedules")}
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        {renderScheduleCell(accountData.account_number, accountData.onDemand, "On Demand Schedules", "schedules")}
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        {renderScheduleCell(accountData.account_number, accountData.tagBased, "Tag Based Workloads", "workloads")}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ ...BODY_CELL_SX, py: 6 }}>
                      <Typography sx={{ ...CELL_TEXT, color: "#64748b" }}>No schedules found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              px: 2,
              py: 1,
              borderTop: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              minHeight: 48,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#2563eb", flexShrink: 0 }} />
              <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#64748b", fontSize: "0.75rem" }}>Checks</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", ml: "auto" }}>
              <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#64748b", fontSize: "0.75rem" }}>Rows per page</Typography>
              <FormControl size="small">
                <Select
                  value={pageSize}
                  onChange={(event) => {
                    const nextSize = Number(event.target.value);
                    setPageSize(nextSize);
                    storePageSize(PAGE_SIZE_KEY, nextSize);
                    setPage(1);
                  }}
                  sx={{
                    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                    fontSize: "0.75rem",
                    height: 28,
                    backgroundColor: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option} sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, fontSize: "0.8rem" }}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#64748b", fontSize: "0.75rem", ml: 0.5 }}>
                {groupedAccounts.total ? `${pageStart}–${pageEnd} of ${groupedAccounts.total}` : "0 of 0"}
              </Typography>
              <IconButton size="small" aria-label="First page" onClick={() => setPage(1)} disabled={page === 1} sx={{ color: page === 1 ? "#d1d5db" : "#64748b" }}>
                <FirstPageIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton size="small" aria-label="Previous page" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} sx={{ color: page === 1 ? "#d1d5db" : "#64748b" }}>
                <ArrowBackIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Next page"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages || totalPages === 0}
                sx={{ color: page >= totalPages || totalPages === 0 ? "#d1d5db" : "#64748b" }}
              >
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Last page"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || totalPages === 0}
                sx={{ color: page >= totalPages || totalPages === 0 ? "#d1d5db" : "#64748b" }}
              >
                <LastPageIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      ) : null}

      <Dialog
        open={Boolean(scheduleModal)}
        onClose={() => setScheduleModal(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "none",
            border: "1px solid #e2e8f0",
            ...ACCOUNT_DETAIL_FONT_SX,
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: "#F98125",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, fontWeight: 700, fontSize: { xs: "1.125rem", sm: "1.25rem" }, color: "#FFFFFF", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
              {scheduleModal?.title}
            </Typography>
            <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, mt: 0.75, color: "rgba(255,255,255,0.9)", fontSize: "0.875rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {accountLabels.numberHeader}:{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
                {scheduleModal?.accountNumber || "N/A"}
              </Box>
            </Typography>
          </Box>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setScheduleModal(null)}
            style={{
              padding: 0,
              margin: 0,
              border: "none",
              background: "transparent",
              color: "#FFFFFF",
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </Box>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, backgroundColor: "#f3f4f6", flex: 1, overflow: "auto" }}>
          <Box sx={{ border: "1px solid #e2e8f0", borderRadius: "12px", backgroundColor: "#fff", overflow: "hidden" }}>
            <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
              <Table size="small" sx={{ width: "100%", minWidth: 420, tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "62%" }} />
                  <col style={{ width: "38%" }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...HEADER_CELL_SX, pl: 2 }}>Schedule Name</TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(scheduleModal?.schedules || []).map((schedule) => (
                    <TableRow key={schedule.schedule_id} sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                      <TableCell sx={{ ...BODY_CELL_SX, pl: 2 }}>
                        <TruncatedTooltipText sx={CELL_TEXT}>{formatScheduleName(schedule)}</TruncatedTooltipText>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        {renderChecksChip(schedule, () => setScheduleModal(null))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ScheduleList;
